// src/pages/ReportPreviewPage.jsx
import React, { useState, useEffect, useMemo, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { track } from "../utils/track";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";

import {
  Loader2,
  Lock,
  Sparkles,
  AlertCircle,
  Zap,
  Users,
  Mail,
  TrendingUp,
  ArrowRight,
  Check,
  Bot,
  LineChart,
  Target,

  FileText,
  Workflow,
  Brain,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import {
  formatCurrency,
  computeY1ROI,
  computePageEstimate,
  formatIndustryLabel,
  possessive,
} from "../utils/reportHelpers";
import { LanguageContext } from "../App";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";

// Removed: fake early bird pricing, consultant anchor, countdown timer

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay },
});

// Testimonials removed — awaiting real customer results

const ReportPreviewPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [email, setEmail] = useState("");
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [pricing, setPricing] = useState({ price: 149, earlyBird: false, spotsRemaining: 0 });
  // Round-26 (Sarah R25): the preview-page bottom badge had a hardcoded
  // "348+ vetted tools" literal that drifted from the live catalog count
  // (351 elsewhere). Same pattern as R21.1's loader fix: fetch from
  // /api/tools/stats once on mount, render via state, fall back to 351
  // when the API is unreachable so the badge never blanks.
  const [liveToolCount, setLiveToolCount] = useState(351);

  useEffect(() => {
    fetch(`${API_BASE}/api/pricing/current`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.price === 'number') setPricing(d); })
      .catch(() => {});
    fetch(`${API_BASE}/api/tools/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.total) setLiveToolCount(d.total); })
      .catch(() => {});
  }, []);

  // R31.7e + R35.1 (paid-traffic launch wiring): fire BOTH a Google
  // Ads conversion event and a GA4 custom event on /report-preview
  // mount, exactly once per reportId.
  //
  // Sarah's R35 verdict caught the original wiring: I was sending
  // event-name "audit_completed" with send_to set, but Google Ads
  // conversion import ONLY fires on the literal event name
  // "conversion" — Sarah saw the GA4 event land but no
  // googleads.g.doubleclick.net conversion ping. Fix: when
  // REACT_APP_GADS_CONVERSION_ID is set, fire a separate event
  // with name "conversion" specifically for Google Ads, AND keep
  // the GA4 audit_completed event for product analytics. Both
  // share the same dedupeKey + transaction_id so refresh-resilient
  // dedup is consistent across both channels.
  //
  // Value $25 is a placeholder representing qualified-lead value
  // (NOT the $149 product price — that's a separate purchase event
  // on /payment-success). Smart Bidding learns from this signal
  // before purchases accumulate.
  useEffect(() => {
    if (!reportId) return;
    const dedupeKey = `conv_audit_completed_${reportId}`;
    let alreadyFired = false;
    try { alreadyFired = !!sessionStorage.getItem(dedupeKey); } catch { /* private mode */ }
    if (alreadyFired) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    try {
      const sharedPayload = {
        value: 25.0,
        currency: "USD",
        transaction_id: reportId,
      };

      // 1) GA4 custom event for product analytics. Always fires.
      window.gtag("event", "audit_completed", sharedPayload);

      // 2) Google Ads conversion event — only fires when the env var
      // is set, AND uses the magic name "conversion" so Google Ads
      // emits the googleads/pagead/conversion/ ping. Without the magic
      // name, Smart Bidding never learns and the campaign Conversions
      // column reads 0 forever.
      const conversionId = process.env.REACT_APP_GADS_CONVERSION_ID;
      if (conversionId) {
        window.gtag("event", "conversion", {
          ...sharedPayload,
          send_to: conversionId,
        });
      }

      try { sessionStorage.setItem(dedupeKey, "1"); } catch { /* private mode */ }
    } catch {
      /* gtag bricked — non-fatal, analytics is best-effort */
    }
  }, [reportId]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchReport = async () => {
      try {
        let response;
        for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            response = await fetch(
              `${API_BASE}/api/audit/report/${reportId}?language=${encodeURIComponent(lang)}`,
              { signal: controller.signal }
            );
            break;
          } catch (fetchErr) {
            if (fetchErr.name === "AbortError") throw fetchErr;
            if (attempt === 1) {
              await new Promise(r => setTimeout(r, 3000));
              continue;
            }
            throw fetchErr;
          }
        }
        if (!response.ok) throw new Error("Report not found.");

        const data = await response.json();
        if (!data.fullReport && !data.businessName) {
          throw new Error("Invalid report data.");
        }

        if (data.isPaid) {
          navigate(`/full-report/${reportId}`);
          return;
        }

        setReportData(data);
        track("report_preview_viewed", { report_id: reportId, industry: data?.industry, tools_count: data?.fullReport?.aiToolsRecommendations?.length });
        // GA4 ecommerce: view_item — fires once when the buyer first sees the
        // teaser. Anchors the Google Ads conversion funnel ("which keyword
        // brought users who actually viewed the product"). Uses pricing.price
        // (149 default, updated by the /api/pricing/current fetch above) so
        // the value reflects the buyer's actual offered price.
        track("view_item", {
          currency: "USD",
          value: pricing.price,
          items: [{
            item_id: reportId,
            item_name: `AI Implementation Blueprint - ${data?.industry || "Custom"}`,
            item_category: "AI Blueprint",
            price: pricing.price,
            quantity: 1,
          }],
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Something went wrong loading your report.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
    return () => controller.abort();
  }, [reportId, navigate, lang]);

  // R47 (free pivot): replaces the $149 payment redirect with an
  // email-unlock modal. Honeypot + min-time gate ride along with the
  // payload — both are server-checked. formStartedAt tracks the time
  // the user landed on this preview page; a sub-10-second unlock
  // is treated as bot and silent-rejected server-side.
  const [showUnlockModal, setShowUnlockModal] = React.useState(false);
  const [unlockEmail, setUnlockEmail] = React.useState("");
  const [unlockHoneypot, setUnlockHoneypot] = React.useState("");
  const [unlockSubmitting, setUnlockSubmitting] = React.useState(false);
  const _previewLandedAtRef = React.useRef(Date.now());

  const goToPayment = () => {
    // R47: kept the function name for backwards-compat with the 5+
    // CTA call sites; behavior changed to open the email-unlock modal.
    track("unlock_click", { page: "report_preview", report_id: reportId, industry: reportData?.industry });
    setShowUnlockModal(true);
  };

  const handleUnlockSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const _email = (unlockEmail || "").trim();
    if (!_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(_email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setUnlockSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/audit/report/${reportId}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: _email,
          honeypot: unlockHoneypot,
          formStartedAt: _previewLandedAtRef.current,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // 400 = disposable email; 429 = rate limit; both have buyer-
        // facing messages on `detail`.
        toast.error(data?.detail || "Couldn't unlock right now. Please try again.");
        return;
      }
      if (!data?.unlocked) {
        // Silent-reject path (honeypot tripped, min-time gate). Show
        // success-shaped toast so we don't telegraph the trap to bots
        // but real users who never tripped it will see the report
        // load normally on next poll.
        toast.success("Your blueprint is being prepared.");
      } else {
        toast.success("Your blueprint is unlocked. Loading now…");
      }
      track("email_unlock_success", { report_id: reportId, industry: reportData?.industry });
      setShowUnlockModal(false);
      // Redirect to full report — the backend has flipped isPaid;
      // FullReportPage will poll until LLM enrichment lands.
      navigate(`/full-report/${reportId}?lang=${lang}`);
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setUnlockSubmitting(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmittingEmail(true);
    try {
      const response = await fetch(`${API_BASE}/api/audit/save-report-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          reportId,
          businessName: reportData?.businessName,
          industry: reportData?.industry,
        }),
      });
      if (response.ok) {
        toast.success("Report link sent! Check your inbox.");
        setEmail("");
      } else {
        // Fallback: copy link to clipboard
        const url = `${window.location.origin}/report-preview/${reportId}`;
        try { await navigator.clipboard.writeText(url); } catch {}
        toast.success("Link copied to clipboard! Bookmark this page to come back later.");
      }
    } catch (err) {
      // Fallback: copy link to clipboard
      const url = `${window.location.origin}/report-preview/${reportId}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Link copied to clipboard! Bookmark this page to come back later.");
    } finally {
      setSubmittingEmail(false);
    }
  };

  const {
    businessName,
    industry,
    fullReport,
    aiAssistant,
    businessType,
    ecommercePlatform,
    // R35.1: subVertical round-trips from the audit submission so PM
    // operators see "Property Management Operators" in the descriptor
    // instead of the brokerage default "Real Estate Brokers & Agents."
    subVertical,
  } = reportData || {};

  const matchedProblems = fullReport?.ownerProblems?.matched_problems || [];
  const phases = fullReport?.implementationRoadmap?.phases || [];
  const tools = fullReport?.aiToolsRecommendations || reportData?.previewReport?.recommendedTools || [];
  const emergingTools = fullReport?.emergingTools || [];
  const playbooks = fullReport?.playbooks || [];
  const adoptionChecklist = fullReport?.adoptionChecklist || [];
  const firstPhase = phases[0] || {};

  // Preview stats from the rule-engine (populated even pre-payment) so the
  // hero shows the buyer's REAL phases / tools / playbooks / annual upside
  // instead of falling back to hardcoded "3 / 5 / 3 / $22,500" placeholders
  // that undersell what they're about to unlock. previewStats is None on
  // legacy preview rows — fall back to fullReport-derived counts then to
  // the historical defaults as a last resort.
  const previewStats = reportData?.previewReport?.previewStats || {};

  const playbookCount = playbooks.length || previewStats.playbookCount || fullReport?.rankedBlueprints?.length || 0;
  const checklistCount = adoptionChecklist.length;
  const totalToolsCount = tools.length || previewStats.toolCount || 0;
  const emergingCount = emergingTools.length;
  const phaseCount = phases.length || previewStats.phaseCount || 0;
  // Annual upside — Round-21 (Sarah R20): pin to roi.canonical_annual so
  // preview tile matches the full-report hero and the landing-page tile.
  // Previously this re-derived recurring × 12 which diverged from the
  // hero's "/year recoverable" canonical. Falls back to recurring × 12
  // only on legacy reports written before R21.
  const annualUpsideFromFull = fullReport?.roiProjections
    ? (fullReport.roiProjections.canonical_annual
       || fullReport.roiProjections.cost_of_inaction_12m
       || ((fullReport.roiProjections.estimated_monthly_savings || 0)
           + (fullReport.roiProjections.estimated_monthly_revenue_uplift || 0)) * 12)
    : 0;
  const annualUpsidePreview = previewStats.annualUpside || 0;

  const problem1 = matchedProblems[0]?.problem || "manual processes";
  const problem2 = matchedProblems[1]?.problem || "customer retention";
  const firstPhaseFocus = firstPhase.focus || "foundation and quick wins";

  const roiProjections = fullReport?.roiProjections || {
    projectedLiftBand: "Moderate",
    approximateImpactIndex: 0.25,
  };
  const impactIdx = roiProjections.approximateImpactIndex ?? 0.25;
  const { y1Net } = computeY1ROI(phases, impactIdx, fullReport?.roiProjections);

  // Critical Finding card — most potent data for conversion
  const topProblem = matchedProblems[0] || {};
  const topProblemText = topProblem.problem || null;
  const topToolName = tools[0]?.name || tools[0]?.tool_name || null;
  const topPlaybookName = playbooks[0]?.name || null;
  const cost12m =
    fullReport?.roiProjections?.cost_of_inaction_12m ||
    (roiProjections?.estimated_monthly_savings ?? roiProjections?.estimatedMonthlySavings ?? 0) * 12;
  const monthlyLoss = cost12m > 0 ? Math.round(cost12m / 12) : null;

  const pageEstimate = useMemo(
    () => computePageEstimate(fullReport, phases, tools, matchedProblems),
    [fullReport, phases, tools, matchedProblems]
  );

  const displayPrice = pricing.price;

  const assistantLabel =
    aiAssistant === "grok"
      ? "Grok"
      : aiAssistant === "chatgpt"
      ? "ChatGPT"
      : aiAssistant === "claude"
      ? "Claude"
      : aiAssistant === "gemini"
      ? "Gemini"
      : "your AI assistant";

  const quickWin = useMemo(() => {
    const t = tools[0];
    if (!t) return null;
    return {
      toolName: t.name || t.tool_name || "Your top AI tool",
      setupHours: t.setup_time_hours ?? t.setupTimeHours ?? 2,
      timeToValueDays: t.time_to_value_days ?? t.timeToValueDays ?? 14,
      outcome: t.why || t.successStory || "Start seeing results in your first week.",
    };
  }, [tools]);

  // Audience noun: derive from INDUSTRY first ("Dental Practice Owners",
  // "HVAC Business Owners", etc.). The earlier version privileged businessType
  // and reduced every services vertical to "Service Business Owners" — a
  // dental buyer would read "specifically for Service Business Owners" and
  // pattern-match the report as generic. Industry is the more specific axis
  // and is what the buyer self-selected on Step 1. We only fall back to
  // businessType-flavored labels when industry is missing or 'other'.
  const businessDescriptor = (() => {
    const ind = (industry || "").toLowerCase();
    const sub = (subVertical || "").toLowerCase();
    const INDUSTRY_AUDIENCE = {
      dental:                "Dental Practice Owners",
      healthcare:            "Healthcare Practice Owners",
      legal:                 "Law Firm Owners",
      accounting:            "Accounting Firm Owners",
      real_estate:           "Real Estate Brokers & Agents",
      // R35.1 (Sarah R35 verdict): PM sub-vertical override. Sarah's
      // walk caught the preview hero rendering "specifically for Real
      // Estate Brokers & Agents" to a Cedar Ridge PM operator who
      // arrived via /pm — same defect class as the original Step 1
      // card bug. Lookup checks subVertical FIRST so PM personas
      // get the right copy on the first surface they see post-audit.
      property_management:   "Property Management Operators",
      hvac:                  "HVAC & Home Services Owners",
      restaurant:            "Restaurant Owners",
      hospitality:           "Hospitality Operators",
      retail:                "Retail Store Owners",
      ecommerce:             "Ecommerce Store Owners",
      shopify:               "Shopify Store Owners",
      construction:          "Construction & Contracting Owners",
      auto_repair:           "Auto Repair Shop Owners",
      med_spa:               "Med Spa & Salon Owners",
      manufacturing:         "Manufacturing Operators",
      finance:               "Finance & Bookkeeping Owners",
      credit_repair:         "Credit Repair Practice Owners",
      professional_services: "Professional Services Owners",
      saas:                  "SaaS Founders",
      technology:            "Technology Founders",
      education:             "Education Operators",
    };
    // Sub-vertical wins when present and mapped — e.g., real_estate
    // + property_management → "Property Management Operators."
    if (sub && INDUSTRY_AUDIENCE[sub]) return INDUSTRY_AUDIENCE[sub];
    if (ind && ind !== "other" && INDUSTRY_AUDIENCE[ind]) return INDUSTRY_AUDIENCE[ind];
    // Industry-known but unmapped: humanize the slug.
    if (ind && ind !== "other") {
      const human = ind.replace(/[_-]+/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      return `${human} Business Owners`;
    }
    // Fall back to businessType only when industry is missing/other.
    if (businessType === "ecommerce" || businessType === "both") {
      if (ecommercePlatform === "shopify")     return "Shopify Store Owners";
      if (ecommercePlatform === "woocommerce") return "WooCommerce Store Owners";
      if (ecommercePlatform === "bigcommerce") return "BigCommerce Store Owners";
      if (ecommercePlatform === "squarespace") return "Squarespace Store Owners";
      if (ecommercePlatform === "wix")         return "Wix Store Owners";
      return "Ecommerce Store Owners";
    }
    if (businessType === "services") return "Service Business Owners";
    return "Small Business Owners";
  })();

  const getIndustryBonus = () => {
    switch (industry?.toLowerCase()) {
      case "healthcare":
        return {
          title: "HIPAA-Safe AI Toolkit",
          desc: "AI tools and workflows pre-vetted for healthcare compliance.",
        };
      case "finance":
        return {
          title: "Finance AI Compliance Guide",
          desc: "AI implementation guidance with financial regulation guardrails.",
        };
      case "education":
        return {
          title: "EdTech AI Playbook",
          desc: "AI tools and strategies tailored for education providers.",
        };
      default:
        return {
          title: `${formatIndustryLabel(industry, "Your Industry", subVertical)} AI Insights`,
          desc: `Benchmarks and AI trends specific to ${formatIndustryLabel(industry, "your business", subVertical).toLowerCase()}.`,
        };
    }
  };
  const industryBonus = getIndustryBonus();

  const truncate = (str, maxLen) =>
    typeof str === "string" && str.length > maxLen
      ? `${str.substring(0, maxLen)}...`
      : str;

  const FAQS = [
    {
      q: "What exactly is in the blueprint?",
      a: `Your blueprint includes a ${phaseCount || 3}-phase AI implementation roadmap, done-for-you AI playbooks for your top problems, a custom prompt library tuned to ${assistantLabel}, a 90-day adoption checklist, ROI projections, and tool recommendations — all specific to your business, industry, and budget.`,
    },
    {
      q: "How is this different from generic AI guides?",
      a: "Everything is generated from your specific inputs using a proprietary scoring engine matched against our full database of vetted AI tools. The personalization layer rewrites your prompts, playbooks, and recommendations to your exact business size, budget, industry, and problems. There are no generic templates.",
    },
    {
      q: "What if I'm not happy with my blueprint?",
      a: "You're covered by our 60-Day Implementation Guarantee. Follow Phase 1 of your blueprint — if you don't see measurable value, email us and we'll troubleshoot with you or refund you in full.",
    },
    {
      q: "Do I need to be technical to use this?",
      a: "Not at all. The playbooks are written for business owners, not developers. Each step tells you exactly what to do, which tool to use, and what outcome to expect — no coding or AI expertise required.",
    },
    {
      q: "How long until I see results?",
      a: `Most owners see their first win within ${firstPhase.weeks || 2} weeks by following Phase 1 alone. The full roadmap is designed to compound over 90 days — small actions that build into a real AI advantage.`,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#3b82f6] h-12 w-12 mb-4 mx-auto" />
          <h2 className="text-xl font-semibold text-white">
            Building your AI Implementation Blueprint&hellip;
          </h2>
          <p className="text-slate-400 text-sm mt-2">
            Scoring our vetted AI tools against your business profile…
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            Something went wrong
          </h2>
          <p className="text-slate-300 mb-2">{error}</p>
          <p className="text-slate-500 text-sm mb-6">
            Your responses were saved — try again or start a new audit below.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.location.reload()}
              className="bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold rounded-full"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate("/audit")}
              variant="outline"
              className="border-white/15 text-slate-300 hover:bg-white/5 rounded-full"
            >
              Start New Audit
            </Button>
          </div>
          <p className="text-slate-600 text-xs mt-6">
            Need help? Email us at{" "}
            <a href="mailto:support@aiworkshopllc.com" className="text-slate-400 underline">
              support@aiworkshopllc.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a] font-sans text-white">
      <Helmet>
        <title>
          Your AI Implementation Preview | {businessName || "Your Business"}
        </title>
        <meta
          name="description"
          content={`AI Implementation Blueprint for ${
            businessName || "your business"
          } — roadmap, tools, prompts, and ROI projections.`}
        />
      </Helmet>

      {/* Dot-grid background */}
      <div
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, white 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
        aria-hidden="true"
      />

      <Header />

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-950/95 backdrop-blur-xl border-t border-white/8 p-3 z-50 md:hidden shadow-[0_-4px_24px_-1px_rgba(16,185,129,0.15)]">
        <div className="text-center mb-1">
          <p className="text-xs text-slate-400">
            60-Day Implementation Guarantee &middot; Instant access
          </p>
        </div>
        <Button
          onClick={goToPayment}
          className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 font-bold h-12 shadow-lg rounded-full transition-all"
        >
          {businessName
            ? `Unlock ${possessive(businessName)} Blueprint`
            : "Unlock My Blueprint"}{" "}
          &mdash; {formatCurrency(displayPrice)}
        </Button>
      </div>

      <main className="pt-28 pb-36 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">

        {/* ── Hero ── */}
        <motion.div {...fadeUp(0)} className="text-center mb-10">
          <Badge className="mb-5 bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-5 py-1.5 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            AI-Generated &bull; Personalized to {businessName || "Your Business"}
          </Badge>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 tracking-tight font-['Space_Grotesk'] bg-gradient-to-r from-white via-slate-200 to-blue-400 bg-clip-text text-transparent">
            {businessName
              ? `${possessive(businessName)} AI Blueprint Is Ready`
              : "Your AI Blueprint Is Ready"}
          </h1>

          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-7">
            A complete {phaseCount || 3}-phase AI implementation plan built
            specifically for {businessDescriptor} — with tool recommendations,
            done-for-you playbooks, and a 90-day checklist your team can execute
            starting this week.
          </p>

          {/* Price */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
            <span className="text-[#3b82f6] font-bold text-lg">
              {formatCurrency(displayPrice)} today
            </span>
            <span className="text-xs text-blue-400 font-semibold">
              Launch pricing — one-time, no subscription
            </span>
          </div>

          {/* Primary CTA */}
          <Button
            onClick={goToPayment}
            className="bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 text-lg font-bold px-10 py-6 shadow-2xl shadow-blue-500/20 hover:shadow-blue-500/40 transition-all rounded-full mb-4 animate-pulse hover:animate-none"
          >
            Get My Blueprint &mdash; Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>

          {/* Guarantee badge */}
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-xs text-slate-300">
            <Shield className="w-3.5 h-3.5 text-[#3b82f6] flex-shrink-0" />
            60-Day Implementation Guarantee &middot; Instant PDF access &middot; No subscription
          </div>
        </motion.div>

        {/* ── "Your Report Is Built" Proof Strip ── */}
        <motion.div {...fadeUp(0.1)} className="mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {
                icon: <Workflow className="w-5 h-5 text-[#3b82f6]" />,
                value: `${phaseCount || "—"} Phases`,
                label: "Implementation roadmap",
                color: "emerald",
              },
              {
                icon: <Zap className="w-5 h-5 text-blue-400" />,
                value: `${totalToolsCount || "—"} Tools`,
                label: "Curated for your stack",
                color: "blue",
              },
              {
                icon: <Brain className="w-5 h-5 text-purple-400" />,
                value: `${playbookCount || "—"} Playbooks`,
                label: "Done-for-you workflows",
                color: "purple",
              },
              {
                // Annual-upside priority: real fullReport ROI > rule-engine
                // previewStats > legacy y1Net fallback. Each tier is a real
                // personalized number; the "$22,500 generic" placeholder
                // (legacy default of computeY1ROI when fullReport is absent)
                // is gone — buyers now see what they're actually unlocking
                // before payment instead of a misleading floor.
                icon: <BarChart3 className="w-5 h-5 text-[#3b82f6]" />,
                value: annualUpsideFromFull > 0
                  ? formatCurrency(annualUpsideFromFull)
                  : annualUpsidePreview > 0
                  ? formatCurrency(annualUpsidePreview)
                  : y1Net > 0
                  ? formatCurrency(y1Net)
                  : "Personalized in full report",
                label: "Est. annual value unlocked",
                color: "emerald",
              },
            ].map(({ icon, value, label, color }, i) => (
              <div
                key={i}
                className={`bg-slate-900/70 backdrop-blur-sm border border-white/10 rounded-2xl p-4 text-center border-t-2 ${
                  color === "emerald"
                    ? "border-t-blue-500"
                    : color === "blue"
                    ? "border-t-blue-500"
                    : "border-t-purple-500"
                }`}
              >
                <div className="flex justify-center mb-2">{icon}</div>
                <div className="text-xl font-bold text-white">{value}</div>
                <div className="text-xs text-slate-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Critical Finding Card ── */}
        {topProblemText && (
          <motion.div {...fadeUp(0.15)} className="mb-10">
            <div
              className="rounded-2xl border border-red-500/30 overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.07) 0%, rgba(15,23,42,0.97) 65%)" }}
            >
              {/* Header */}
              <div className="px-6 pt-5 pb-3 flex items-center gap-3 border-b border-white/8">
                <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
                <span className="text-red-400 text-sm font-semibold uppercase tracking-wider">
                  Critical Finding — {businessName || "Your Business"}
                </span>
                <Badge className="ml-auto bg-red-500/10 text-red-400 border border-red-500/20 text-xs">
                  {matchedProblems.length || 1} Problem{matchedProblems.length !== 1 ? "s" : ""} Identified
                </Badge>
              </div>

              {/* Visible finding */}
              <div className="px-6 py-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-400 uppercase tracking-wider mb-1 font-semibold">Your #1 Identified Problem</div>
                    <p className="text-white font-semibold text-base leading-snug mb-3">{topProblemText}</p>
                    {monthlyLoss && (
                      <div className="inline-flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-1.5">
                        <TrendingUp className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                        <span className="text-red-300 text-sm font-semibold">
                          Estimated cost if unresolved: {formatCurrency(monthlyLoss)}/month
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Blurred rows — playbook + top tool */}
              <div className="relative px-6 pb-5 space-y-3">
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-b-2xl"
                  style={{ backdropFilter: "blur(5px)", background: "rgba(15,23,42,0.55)" }}>
                  <Lock className="w-5 h-5 text-[#3b82f6] mb-1" />
                  <span className="text-xs text-slate-300 font-medium">
                    {matchedProblems.length > 1 ? `+${matchedProblems.length - 1} more findings` : "Full fix plan"} unlocked after purchase
                  </span>
                </div>
                <div className="flex items-center gap-3 bg-slate-950/50 rounded-xl p-4 border border-white/8 select-none">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Playbook assigned to fix this</div>
                    <div className="text-white font-semibold text-sm">{topPlaybookName || "AI Automation Playbook"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-slate-950/50 rounded-xl p-4 border border-white/8 select-none">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-[#3b82f6]" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Top recommended tool</div>
                    <div className="text-white font-semibold text-sm">{topToolName || "Your primary AI tool"}</div>
                  </div>
                </div>
              </div>

              <div className="px-6 pb-5 pt-2">
                <Button
                  onClick={goToPayment}
                  className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 font-bold py-4 rounded-full text-base shadow-lg hover:shadow-blue-500/25 transition-all"
                >
                  See All {matchedProblems.length || 3} Findings + Your Fix Plan — {formatCurrency(displayPrice)}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-xs text-slate-500 text-center mt-2">
                  {pageEstimate} pages · instant access · 60-Day Implementation Guarantee
                  {emergingCount > 0 ? ` · ${emergingCount} hidden-gem tools` : ""}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Quick Win Card ── */}
        {quickWin && (
          <motion.div {...fadeUp(0.16)} className="mb-8">
            <div
              className="rounded-2xl border border-amber-500/25 overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(15,23,42,0.99) 100%)" }}
            >
              <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-white">Your Fastest First Win</span>
                </div>
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/20 text-xs">
                  Start this week
                </Badge>
              </div>
              <div className="p-6 flex flex-col sm:flex-row items-start gap-5">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/20 flex items-center justify-center flex-shrink-0 text-xl">
                  ⚡
                </div>
                <div className="flex-1">
                  <p className="text-base font-bold text-white mb-1">{quickWin.toolName}</p>
                  <p className="text-xs text-slate-400 mb-3">
                    Setup: ~{quickWin.setupHours}h &bull; First results in ~{quickWin.timeToValueDays} days
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed">{quickWin.outcome}</p>
                </div>
                <Button
                  onClick={goToPayment}
                  size="sm"
                  className="flex-shrink-0 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/25 rounded-full text-xs px-4"
                >
                  Get the setup steps
                  <ArrowRight className="ml-1.5 w-3 h-3" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Top Tool Preview ── */}
        {tools[0] && (
          <motion.div {...fadeUp(0.18)} className="mb-8">
            <div
              className="rounded-2xl border border-blue-500/25 overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(15,23,42,0.99) 100%)" }}
            >
              <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#3b82f6]" />
                  <span className="text-sm font-semibold text-white">
                    Your #1 Recommended AI Tool
                  </span>
                </div>
                <Badge className="bg-blue-500/15 text-[#3b82f6] border border-blue-500/20 text-xs">
                  {totalToolsCount} tools in full report
                </Badge>
              </div>
              <div className="p-6">
                {/* Tool identity */}
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/20 flex items-center justify-center flex-shrink-0 text-xl">
                    🛠️
                  </div>
                  <div>
                    <p className="text-lg font-bold text-white">{tools[0]?.name || tools[0]?.tool_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {tools[0]?.category || "AI Tool"} &bull; {tools[0]?.pricing || tools[0]?.estimatedMonthlyCost || "Varies"}
                      {tools[0]?.hours_saved ? ` · ~${tools[0].hours_saved}hrs saved/week` : ""}
                    </p>
                  </div>
                </div>
                {/* Why selected — real copy */}
                <div className="bg-slate-950/50 border border-white/8 rounded-xl p-4 mb-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1 font-semibold">Why it was selected for you</p>
                  <p className="text-sm text-slate-200 leading-relaxed">
                    {truncate(tools[0]?.why_selected || tools[0]?.why || tools[0]?.matchReason || tools[0]?.description || "Matched to your top business problem and budget.", 180)}
                  </p>
                </div>
                {/* Setup steps teaser — locked */}
                <div className="relative rounded-xl overflow-hidden border border-white/10 mb-5">
                  <div className="p-4 space-y-2 select-none pointer-events-none"
                    style={{ filter: "blur(4px)", opacity: 0.5 }}>
                    {["Open account & connect your existing tools", "Configure for your industry workflow", "Run first automation and verify results"].map((s, i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-950/50 rounded-lg p-2.5">
                        <span className="w-5 h-5 rounded-full bg-blue-500/20 text-[#3b82f6] text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                        <span className="text-sm text-slate-300">{s}</span>
                      </div>
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-2 bg-slate-950/90 border border-white/10 rounded-xl px-4 py-2.5 shadow-lg">
                      <Lock className="w-3.5 h-3.5 text-[#3b82f6]" />
                      <span className="text-xs font-semibold text-slate-200">6 setup steps unlocked after purchase</span>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={goToPayment}
                  className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 font-bold py-3 rounded-full text-sm shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  Get Setup Steps + {totalToolsCount - 1} More Tools — {formatCurrency(displayPrice)}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Phase 1 Action Preview ── */}
        {firstPhase.focus && (
          <motion.div {...fadeUp(0.19)} className="mb-10">
            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="px-6 py-4 bg-slate-900/70 border-b border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#3b82f6]" />
                  <span className="text-sm font-semibold text-white">
                    Phase 1 — Starts This Week
                  </span>
                  <Badge className="bg-white/8 text-slate-300 border-0 text-xs ml-1">{firstPhase.weeks || 4} weeks</Badge>
                </div>
                <Badge className="bg-blue-500/15 text-[#3b82f6] border border-blue-500/20 text-xs">
                  {phases.length} phases total
                </Badge>
              </div>
              <div className="p-6">
                {/* Phase focus — visible */}
                <div className="flex items-start gap-3 bg-blue-500/8 border border-blue-500/15 rounded-xl p-4 mb-4">
                  <CheckCircle2 className="w-4 h-4 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-[#3b82f6] font-semibold uppercase tracking-wider mb-0.5">Phase Focus</p>
                    <p className="text-sm text-white font-medium">{truncate(firstPhase.focus, 120)}</p>
                  </div>
                </div>
                {/* Week 1 goal — visible */}
                {firstPhase.expected_impact && (
                  <div className="flex items-start gap-3 bg-slate-950/50 rounded-xl p-4 mb-4 border border-white/8">
                    <TrendingUp className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-blue-400 font-semibold uppercase tracking-wider mb-0.5">Expected Impact</p>
                      <p className="text-sm text-slate-200">{truncate(firstPhase.expected_impact, 120)}</p>
                    </div>
                  </div>
                )}
                {/* Data foundation warning */}
                {firstPhase.data_foundation_required && (
                  <div className="flex items-start gap-3 bg-amber-500/8 border border-amber-500/20 rounded-xl p-4 mb-4">
                    <span className="text-amber-400 text-base flex-shrink-0">⚠️</span>
                    <div>
                      <p className="text-xs text-amber-400 font-semibold uppercase tracking-wider mb-0.5">Data Foundation Required</p>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        Your blueprint includes a data clean-up sprint as a prerequisite to Phase 1 — so your AI tools have reliable inputs from day one. The full report shows exactly which records to fix and which tool does it automatically.
                      </p>
                    </div>
                  </div>
                )}
                {/* Tools in phase — blurred */}
                <div className="relative rounded-xl overflow-hidden border border-white/10">
                  <div className="p-4 space-y-2 select-none pointer-events-none"
                    style={{ filter: "blur(4px)", opacity: 0.45 }}>
                    {[
                      `Tool stack for Phase 1 (${(firstPhase.tools || []).length || tools.slice(0,3).length} tools)`,
                      "Week-by-week task schedule",
                      "Cost breakdown & ROI milestones",
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-950/50 rounded-lg px-4 py-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3b82f6] flex-shrink-0" />
                        <span className="text-sm text-slate-300">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={goToPayment}
                      className="flex items-center gap-2 bg-slate-950/90 border border-white/10 rounded-xl px-4 py-2.5 shadow-lg hover:border-blue-500/50 transition-colors"
                    >
                      <Lock className="w-3.5 h-3.5 text-[#3b82f6]" />
                      <span className="text-xs font-semibold text-slate-200">Unlock full Phase 1 plan</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Save Report Link — prominent inline card ── */}
        <motion.div {...fadeUp(0.19)} className="mb-8">
          <div className="rounded-2xl border border-blue-500/25 p-6" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(15,23,42,0.99) 100%)' }}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white mb-0.5">Not ready to unlock right now?</p>
                <p className="text-xs text-slate-400">Enter your email and we&apos;ll send you this report link so you can come back when the timing is right.</p>
              </div>
              <form onSubmit={handleEmailSubmit} className="flex gap-2 w-full sm:w-auto flex-shrink-0">
                <input
                  type="email"
                  placeholder="you@yourbusiness.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 sm:w-52 h-9 bg-slate-950/50 border border-white/15 rounded-lg px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
                />
                <button
                  type="submit"
                  disabled={submittingEmail}
                  className="h-9 px-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm font-medium rounded-full transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  {submittingEmail ? (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                  ) : 'Save link'}
                </button>
              </form>
            </div>
          </div>
        </motion.div>

        {/* ── Cohort Proof + Countdown ── */}
        <motion.div
          {...fadeUp(0.2)}
          className="flex flex-wrap items-center justify-center gap-4 mb-14"
        >
          <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-4 py-2 text-sm">
            <Users className="w-4 h-4 mr-2 inline" />
            {liveToolCount}+ vetted tools &bull; Personalized to your inputs &bull; Industry benchmarks applied
          </Badge>
        </motion.div>

        {/* ── Locked Roadmap Card ── */}
        <motion.div {...fadeUp(0.22)} className="mb-8">
          <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 overflow-hidden relative rounded-3xl">
            {/* Overlay */}
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 text-center"
              style={{ background: "linear-gradient(to bottom, rgba(15,23,42,0.82) 0%, rgba(15,23,42,0.97) 60%, #0f172a 100%)", backdropFilter: "blur(2px)" }}>
              <div className="w-16 h-16 bg-blue-500/15 rounded-2xl flex items-center justify-center mb-5 border border-blue-500/20">
                <Lock className="w-8 h-8 text-[#3b82f6]" />
              </div>
              <Badge className="mb-3 bg-blue-500/10 text-[#3b82f6] border border-blue-500/20 text-xs">
                {phaseCount || 3}-Phase Roadmap &bull; {totalToolsCount} Tools &bull; {firstPhase.weeks || 4}+ weeks
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold mb-3 text-white">
                {businessName
                  ? `${possessive(businessName)} AI Implementation Roadmap Is Ready`
                  : "Your AI Implementation Roadmap Is Ready"}
              </h3>
              <p className="text-slate-300 mb-2 max-w-md text-sm leading-relaxed">
                We've built a complete phase-by-phase AI plan tailored to your{" "}
                {formatIndustryLabel(industry, '', subVertical).toLowerCase() || "small"} business — each phase includes specific tools,
                expected outcomes, and a week-by-week execution schedule.
              </p>
              <p className="text-slate-500 text-xs mb-6">
                A single AI consulting session runs $300–$500/hr.
                Get this blueprint for {formatCurrency(displayPrice)} — one-time, no subscription.
              </p>
              <Button
                onClick={goToPayment}
                className="bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 text-lg font-bold px-10 py-5 shadow-2xl hover:shadow-blue-500/40 transition-all rounded-full"
              >
                Unlock the Full Roadmap &mdash; {formatCurrency(displayPrice)}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                60-Day Implementation Guarantee &mdash; follow Phase 1 or full refund
              </p>
            </div>
            {/* Blurred real content */}
            <CardHeader className="border-b border-white/8">
              <CardTitle className="text-slate-300">Your AI Implementation Roadmap</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="blur-sm select-none space-y-4 pointer-events-none">
                {(phases.length > 0 ? phases : [
                  { name: "Phase 1 – Quick Wins", focus: "foundation and quick wins", weeks: 4 },
                  { name: "Phase 2 – Scale & Automate", focus: "expand what's working", weeks: 6 },
                  { name: "Phase 3 – Optimize & Grow", focus: "compound your AI advantage", weeks: 6 },
                ]).map((p, i) => (
                  <div key={i} className={`rounded-xl p-4 border ${
                    i === 0 ? "bg-blue-900/20 border-blue-700/30" :
                    i === 1 ? "bg-blue-900/20 border-blue-700/30" :
                    "bg-purple-900/20 border-purple-700/30"
                  }`}>
                    <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${
                      i === 0 ? "text-[#3b82f6]" : i === 1 ? "text-blue-400" : "text-purple-400"
                    }`}>
                      Phase {i + 1} &bull; {p.weeks} weeks
                    </div>
                    <p className="font-semibold text-white">{p.name}</p>
                    <p className="text-sm text-slate-300 mt-0.5">Focus: {p.focus}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* ── Locked Prompt Pack Card ── */}
        <motion.div {...fadeUp(0.25)} className="mb-14">
          <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 overflow-hidden relative rounded-3xl">
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 text-center"
              style={{ background: "linear-gradient(to bottom, rgba(15,23,42,0.82) 0%, rgba(15,23,42,0.97) 60%, #0f172a 100%)", backdropFilter: "blur(2px)" }}>
              <div className="w-16 h-16 bg-blue-500/15 rounded-2xl flex items-center justify-center mb-5 border border-blue-500/20">
                <Brain className="w-8 h-8 text-blue-400" />
              </div>
              <Badge className="mb-3 bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs">
                Custom for {assistantLabel} &bull; {matchedProblems.length || "Multiple"} problems addressed
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold mb-3 text-white">
                {businessName
                  ? `${possessive(businessName)} Custom Prompt Pack`
                  : "Your Custom Prompt Pack"}
              </h3>
              <p className="text-slate-300 mb-6 max-w-md text-sm leading-relaxed">
                Copy-paste prompts written for {assistantLabel} — each one
                tailored to your specific problems and tools. Drop them in and get
                results in minutes, not hours.
              </p>
              <Button
                onClick={goToPayment}
                className="bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-700 text-white text-lg font-bold px-10 py-5 shadow-2xl hover:shadow-blue-500/30 transition-all rounded-full"
              >
                Unlock Prompt Pack &mdash; Included Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-xs text-slate-500 mt-3">Included with your blueprint — no extra charge</p>
            </div>
            <CardHeader className="border-b border-white/8">
              <CardTitle className="text-slate-300">Your Custom Prompt Pack for {assistantLabel}</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="blur-sm select-none space-y-4 pointer-events-none">
                {[problem1, problem2, matchedProblems[2]?.problem || "team productivity"].map((prob, i) => (
                  <div key={i} className="bg-slate-950/50 rounded-xl p-4 border border-white/8">
                    <div className="text-xs text-slate-400 mb-1">Prompt {i + 1} of {Math.max(3, matchedProblems.length)}</div>
                    <p className="font-semibold text-white text-sm">
                      {assistantLabel} prompt for: {truncate(prob, 60)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* ── Playbook Teaser ── */}
        {playbooks.length > 0 && (
          <motion.div {...fadeUp(0.26)} className="mb-14">
            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="px-6 py-4 bg-slate-900/70 border-b border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#3b82f6]" />
                  <span className="text-sm font-semibold text-white">
                    Preview: {playbooks[0]?.title || "AI Playbook"} — your first playbook
                  </span>
                </div>
                <Badge className="bg-blue-500/15 text-[#3b82f6] border border-blue-500/20 text-xs">
                  {playbookCount} playbooks total
                </Badge>
              </div>
              {/* Partially visible content with hard blur fade */}
              <div className="relative bg-slate-900/70">
                <div className="p-6 space-y-3 pointer-events-none select-none">
                  {playbooks[0]?.outcome && (
                    <div className="flex items-start gap-3 bg-blue-500/8 border border-blue-500/15 rounded-xl p-3">
                      <Target className="w-4 h-4 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-[#3b82f6] font-semibold uppercase tracking-wider mb-0.5">Target Outcome</div>
                        <div className="text-sm text-white">{truncate(playbooks[0].outcome, 80)}</div>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    {(playbooks[0]?.steps || ["Step 1", "Step 2", "Step 3"]).slice(0, 4).map((step, si) => (
                      <div key={si} className="flex items-start gap-3 bg-slate-950/50 rounded-lg p-3">
                        <span className="w-5 h-5 rounded-full bg-blue-500/20 text-[#3b82f6] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          {si + 1}
                        </span>
                        <span className="text-sm text-slate-300 leading-snug">
                          {typeof step === "string" ? truncate(step, 90) : truncate(step?.action || step?.step || String(step), 90)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Fade-out gradient overlay */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-36 flex flex-col items-center justify-end pb-5"
                  style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(15,23,42,0.97) 70%)" }}
                >
                  <button
                    onClick={goToPayment}
                    className="inline-flex items-center gap-2 bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 font-bold text-sm px-6 py-2.5 rounded-full shadow-lg transition-all"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    Unlock all {playbookCount} playbooks &mdash; {formatCurrency(displayPrice)}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Sample Prompt Reveal ── */}
        {matchedProblems[0] && (
          <motion.div {...fadeUp(0.27)} className="mb-14">
            <div className="rounded-2xl border border-blue-500/25 overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(15,23,42,0.98) 100%)" }}>
              <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-semibold text-white">
                    Live Sample — 1 Prompt From Your Library
                  </span>
                </div>
                <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500/20 text-xs">
                  Unlock {matchedProblems.length}+ more
                </Badge>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">
                    Prompt for {assistantLabel}: {truncate(matchedProblems[0].problem, 55)}
                  </span>
                </div>
                <div className="relative bg-slate-950/50 rounded-2xl border border-white/10 p-4 font-mono text-xs text-slate-300 leading-relaxed overflow-hidden">
                  <p className="mb-2 text-slate-400 italic">You are an expert AI consultant helping {businessName || "this business"} ({formatIndustryLabel(industry, "small business", subVertical).toLowerCase()}).</p>
                  <p className="mb-2">The owner's biggest challenge is: <span className="text-[#3b82f6] font-semibold">"{truncate(matchedProblems[0].problem, 100)}"</span></p>
                  <p>Create a practical, step-by-step action plan I can implement this week. Include specific AI tools, estimated time savings, and first actions to take today.</p>
                  {/* Fade out bottom */}
                  <div className="absolute bottom-0 left-0 right-0 h-10"
                    style={{ background: "linear-gradient(to bottom, transparent, rgba(15,23,42,0.98))" }} />
                </div>
                <p className="text-xs text-slate-500 mt-3 mb-4">
                  ☝️ This is one of {matchedProblems.length} prompts in your library — each written for {assistantLabel} using your exact business context.
                </p>
                <Button
                  onClick={goToPayment}
                  className="w-full bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-700 text-white font-bold py-3 rounded-full text-sm shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  Unlock All {matchedProblems.length} Prompts + Full Blueprint — Free
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── What's Inside ── */}
        <motion.div {...fadeUp(0.28)} className="mb-14">
          <h2 className="text-3xl font-bold text-center mb-2 text-white">
            Everything Inside Your Blueprint
          </h2>
          <p className="text-slate-400 text-center text-sm mb-8">
            Every section is specific to {businessName || "your business"} — not
            a template, not generic advice.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                icon: <Workflow className="w-6 h-6 text-[#3b82f6]" />,
                accent: "emerald",
                title: `${phaseCount || 3}-Phase AI Implementation Timeline`,
                desc: `Week-by-week plan with clear goals, deliverables, and milestones — starting with ${truncate(firstPhaseFocus, 40)} in Phase 1. You always know what to do next.`,
              },
              {
                icon: <Zap className="w-6 h-6 text-purple-400" />,
                accent: "purple",
                title: `${playbookCount || "Done-for-You"} AI Playbooks`,
                desc: `Click-and-run workflows for your biggest bottlenecks — like ${truncate(problem1, 35)} and ${truncate(problem2, 35)}. Tools, steps, owners, and expected outcome included.`,
              },
              {
                icon: <Brain className="w-6 h-6 text-blue-400" />,
                accent: "blue",
                title: `Custom ${assistantLabel} Prompt Library`,
                desc: `Copy-paste prompts for ${assistantLabel} written for your exact problems and tool stack. No generic "write a marketing plan" filler — these are ready to use today.`,
              },
              {
                icon: <CheckCircle2 className="w-6 h-6 text-[#3b82f6]" />,
                accent: "emerald",
                title: `90-Day Adoption Checklist${checklistCount > 0 ? ` (${checklistCount} items)` : ""}`,
                desc: "Week-by-week checklist with owner, ops, and marketing tasks — so your team can implement the plan without being AI experts and the blueprint doesn't die in a PDF.",
              },
              {
                icon: <BarChart3 className="w-6 h-6 text-blue-400" />,
                accent: "blue",
                title: "ROI Projections & Tool Cost Analysis",
                desc: `Projected Year 1 upside${y1Net > 0 ? ` of ${formatCurrency(y1Net)}` : ""} with per-tool cost breakdowns — so you know exactly what to budget and what to expect back.`,
              },
              {
                icon: <FileText className="w-6 h-6 text-purple-400" />,
                accent: "purple",
                title: `${totalToolsCount} Curated Tool Recommendations`,
                desc: `Every tool vetted for your industry, budget, and business size — with setup complexity, monthly cost, and expected ROI lift so you can compare and decide fast.`,
              },
            ].map(({ icon, accent, title, desc }, i) => (
              <div
                key={i}
                className={`bg-slate-900/70 backdrop-blur-sm border border-white/10 rounded-2xl p-5 flex items-start gap-4 border-l-2 ${
                  accent === "emerald"
                    ? "border-l-blue-500"
                    : accent === "blue"
                    ? "border-l-blue-500"
                    : "border-l-purple-500"
                } hover:border-white/20 transition-colors`}
              >
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    accent === "emerald"
                      ? "bg-blue-500/15"
                      : accent === "blue"
                      ? "bg-blue-500/15"
                      : "bg-purple-500/15"
                  }`}
                >
                  {icon}
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1 text-sm">{title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Everything Included (Bonuses) ── */}
        <motion.div {...fadeUp(0.3)} className="mb-14">
          <div
            className="rounded-3xl p-8 md:p-12 border border-blue-500/20 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(59,130,246,0.05) 50%, rgba(15,23,42,1) 100%)",
            }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-2 text-white">
              Not a PDF — A Living Implementation Dashboard
            </h3>
            <p className="text-center text-slate-400 text-sm mb-10">
              Your blueprint opens as an interactive report — track progress, launch tools, ask AI questions, and get weekly updates as the AI landscape evolves.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  icon: <Mail className="w-6 h-6 text-[#3b82f6]" />,
                  accent: "emerald",
                  title: "3-Part Email Implementation Series",
                  desc: "Automated emails at Day 3, Day 7, and Day 90 — each with a specific action step, your top tool, and a milestone check-in to keep you moving.",
                },
                {
                  icon: <TrendingUp className="w-6 h-6 text-blue-400" />,
                  accent: "blue",
                  title: "Weekly AI Signals Digest",
                  desc: "Every Monday you get the top 3 trending AI tools for your industry — so your blueprint stays current as the AI landscape evolves.",
                },
                {
                  icon: <Bot className="w-6 h-6 text-purple-400" />,
                  accent: "purple",
                  title: "Ask AI — Report Q&A Chat",
                  desc: "Ask any question about your blueprint inside the report — 'What should I start with?' or 'Explain Phase 1' — and get an answer grounded in your specific data.",
                },
                {
                  icon: <CheckCircle2 className="w-6 h-6 text-amber-400" />,
                  accent: "amber",
                  title: "7-Day Action Guide + Progress Tracker",
                  desc: "Your report opens on a personalized 7-day sprint — Day 1 through Day 7 actions, a week-by-week plan, and a progress bar that tracks every tool and playbook you complete.",
                },
                {
                  icon: <LineChart className="w-6 h-6 text-[#3b82f6]" />,
                  accent: "emerald",
                  title: "Downloadable PDF Blueprint",
                  desc: "Your full blueprint as a formatted PDF you can share with your team — including tool stack, roadmap, and ROI projections — so it lives outside the browser too.",
                },
                {
                  icon: <Sparkles className="w-6 h-6 text-blue-400" />,
                  accent: "blue",
                  title: industryBonus.title,
                  desc: industryBonus.desc,
                },
              ].map(({ icon, accent, title, desc }, i) => (
                <div
                  key={i}
                  className="bg-slate-900/60 backdrop-blur-sm border border-white/10 rounded-2xl p-5 flex flex-col items-start gap-3"
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      accent === "emerald"
                        ? "bg-blue-500/15"
                        : accent === "blue"
                        ? "bg-blue-500/15"
                        : accent === "purple"
                        ? "bg-purple-500/15"
                        : "bg-amber-500/15"
                    }`}
                  >
                    {icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white text-sm mb-1">{title}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Testimonials (removed — awaiting real customer results) ── */}
        <motion.div {...fadeUp(0.33)} className="mb-14">
          <p className="text-center text-slate-500 text-sm py-8">
            We launched recently — real customer results coming soon.
            <br />
            <span className="text-slate-600">60-Day Implementation Guarantee if it doesn't deliver value.</span>
          </p>
        </motion.div>

        {/* ── Value Comparison ── */}
        <motion.div {...fadeUp(0.35)} className="mb-14">
          <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/8 text-center">
              <h3 className="font-bold text-white text-lg">
                {formatCurrency(displayPrice)} vs the alternatives
              </h3>
            </div>
            {/* Column headers — TOP */}
            <div className="grid grid-cols-6 px-6 py-3 border-b border-white/8 bg-slate-950/40">
              <div className="col-span-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Option</div>
              <div className="text-center text-xs text-slate-500 font-medium uppercase tracking-wider">Price</div>
              {["Your industry", "Your tools", "Step-by-step plan", "60-day guarantee"].map((h, i) => (
                <div key={i} className="text-center text-xs text-slate-500 font-medium uppercase tracking-wider leading-tight">
                  {h}
                </div>
              ))}
            </div>
            <div className="divide-y divide-white/8">
              {[
                {
                  label: "Ask ChatGPT / Grok",
                  cost: "Free",
                  checks: [false, false, false, false],
                  highlight: false,
                  note: "Generic advice — same answer for every business, no real data",
                },
                {
                  label: "Hire an AI consultant",
                  cost: "$300–$500/hr",
                  checks: [true, true, true, false],
                  highlight: false,
                },
                {
                  label: "Generic AI course",
                  cost: "$200–$997",
                  checks: [false, false, true, false],
                  highlight: false,
                },
                {
                  label: "Figure it out yourself",
                  cost: "80+ hrs",
                  checks: [false, false, false, false],
                  highlight: false,
                },
                {
                  label: `Your Blueprint`,
                  cost: formatCurrency(displayPrice),
                  checks: [true, true, true, true],
                  highlight: true,
                },
              ].map(({ label, cost, checks, highlight, note }, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-6 px-6 py-4 items-center text-sm ${
                    highlight
                      ? "bg-blue-500/10 border-l-2 border-l-blue-500"
                      : ""
                  }`}
                >
                  <div className={`col-span-2 font-medium ${highlight ? "text-[#3b82f6]" : "text-white"}`}>
                    {label}
                    {highlight && <span className="ml-2 text-[10px] bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-1.5 py-0.5 rounded-full">Best value</span>}
                    {note && <p className="text-[11px] text-slate-500 font-normal mt-0.5">{note}</p>}
                  </div>
                  <div className={`text-center font-semibold ${highlight ? "text-[#3b82f6]" : "text-slate-400"}`}>
                    {cost}
                  </div>
                  {checks.map((ok, j) => (
                    <div key={j} className="flex justify-center">
                      {ok ? (
                        <Check className="w-4 h-4 text-[#3b82f6]" />
                      ) : (
                        <span className="text-slate-600 text-lg leading-none flex items-center justify-center w-4 h-4">
                          &times;
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── FAQ ── */}
        <motion.div {...fadeUp(0.38)} className="mb-14 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8 text-white">
            Frequently Asked Questions
          </h3>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className={`bg-slate-900/70 backdrop-blur-sm border rounded-2xl overflow-hidden transition-colors ${
                  openFaq === i ? "border-blue-500/40" : "border-white/10"
                }`}
              >
                <button
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-3"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-white text-sm">{faq.q}</span>
                  <ChevronRight
                    className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${
                      openFaq === i ? "rotate-90" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-slate-300 text-sm leading-relaxed border-t border-white/8 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Promo Code ── */}
        <motion.div {...fadeUp(0.4)} className="mb-14">
          <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-3xl p-8 text-center max-w-xl mx-auto">
            <p className="text-[#3b82f6] text-xs uppercase tracking-widest mb-2">
              Limited time offer
            </p>
            <h3 className="text-xl font-bold mb-4 text-white">Have a promo code?</h3>
            <div className="flex gap-3">
              <Input
                id="promo-code"
                aria-label="Promo code"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="h-12 bg-slate-950/50 border-white/15 text-white placeholder:text-slate-500"
              />
              <Button
                onClick={() =>
                  navigate(
                    `/payment/${reportId}?promo=${encodeURIComponent(
                      promoCode.trim()
                    )}&lang=${lang}`
                  )
                }
                className="bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 font-bold h-12 px-8 shadow-lg rounded-full"
                disabled={!promoCode.trim()}
              >
                Apply
              </Button>
            </div>
          </div>
        </motion.div>

        {/* ── Cost of Inaction ── */}
        <motion.div {...fadeUp(0.40)} className="mb-14">
          <div className="bg-slate-900/70 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-500/15 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                <TrendingUp className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-white mb-4">
                  What happens if you don&apos;t act on this?
                </h4>
                {/* Resolve the three line items from backend */}
                {(() => {
                  const roi = fullReport?.roiProjections || {};
                  const laborSavings  = roi.estimated_monthly_savings        || 0;
                  const revenueUplift = roi.estimated_monthly_revenue_uplift || 0;
                  const hourlyRate    = roi.labor_benchmark_hourly            || null;
                  const liftPct       = roi.industry_avg_ai_lift_pct         || null;
                  const monthlyTotal  = laborSavings + revenueUplift
                                        || (roi.cost_of_inaction_12m
                                             ? roi.cost_of_inaction_12m / 12
                                             : 0);

                  // Graceful fallback — never show $0
                  if (!monthlyTotal && !laborSavings && !revenueUplift) {
                    return (
                      <p className="text-slate-300 text-sm leading-relaxed mb-3">
                        {monthlyLoss > 0 ? (
                          <>
                            Based on your report, not implementing these tools costs{" "}
                            <span className="text-amber-400 font-semibold">
                              {formatCurrency(monthlyLoss)}/month
                            </span>{" "}
                            in delayed value — that&apos;s{" "}
                            <span className="text-amber-400 font-semibold">
                              {formatCurrency(monthlyLoss * 12)}/year
                            </span>{" "}
                            your competitors aren&apos;t leaving on the table.
                          </>
                        ) : (
                          <>
                            Every month you delay gives competitors using AI a growing
                            efficiency edge. The tools in your blueprint are available
                            now — so is the advantage.
                          </>
                        )}
                      </p>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {/* Heading */}
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
                        Where your money is going right now
                      </div>

                      {/* Labor line item */}
                      {laborSavings > 0 && (
                        <div className="bg-slate-950/60 border border-white/8 rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="text-xs font-semibold text-slate-300 mb-1">
                                ⏱ Labor time cost
                              </div>
                              <div className="text-[11px] text-slate-500 leading-relaxed space-y-0.5">
                                <div>Tasks AI can automate at your business</div>
                                {hourlyRate && (
                                  <div>
                                    × ${hourlyRate}/hr
                                    <span className="text-slate-700 ml-1">(BLS benchmark for your industry)</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-base font-black text-red-400 font-['Space_Grotesk']">
                                {formatCurrency(laborSavings)}/mo
                              </div>
                              <div className="text-[10px] text-slate-600">recoverable</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Revenue uplift line item */}
                      {revenueUplift > 0 && (
                        <div className="bg-slate-950/60 border border-white/8 rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="text-xs font-semibold text-slate-300 mb-1">
                                📈 Revenue left on the table
                              </div>
                              <div className="text-[11px] text-slate-500 leading-relaxed space-y-0.5">
                                <div>Based on your reported revenue</div>
                                {liftPct && (
                                  <div>
                                    × {liftPct.toFixed(1)}% avg AI lift
                                    <span className="text-slate-700 ml-1">(your industry benchmark)</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-base font-black text-orange-400 font-['Space_Grotesk']">
                                {formatCurrency(revenueUplift)}/mo
                              </div>
                              <div className="text-[10px] text-slate-600">estimated upside</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Divider + total */}
                      {monthlyTotal > 0 && (
                        <div className="border-t border-white/8 pt-3 mt-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-bold text-white">Total cost of not acting</div>
                              <div className="text-[11px] text-slate-600 mt-0.5">Conservative estimate · methodology below</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-black text-red-400 font-['Space_Grotesk']">
                                {formatCurrency(monthlyTotal)}/mo
                              </div>
                              <div className="text-sm text-slate-400 font-semibold">
                                {formatCurrency(monthlyTotal * 12)}/yr
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Methodology note */}
                      <div className="text-[10px] text-slate-700 leading-relaxed pt-2 border-t border-white/5">
                        Labor costs use BLS 2025 industry wage benchmarks.
                        Revenue uplift uses conservative adoption-weighted lift
                        estimates from our network. Both are
                        independent lower-bound estimates — actual results vary.
                      </div>

                      {/* Report cost context */}
                      {monthlyTotal > 0 && (
                        <div className="bg-blue-500/8 border border-blue-500/20 rounded-xl px-4 py-3 text-center mt-2">
                          <span className="text-[#3b82f6] font-bold text-sm">Your blueprint costs {formatCurrency(displayPrice)}</span>
                          <span className="text-slate-500 text-xs ml-2">
                            — that&apos;s{" "}
                            {((displayPrice / (monthlyTotal * 12)) * 100).toFixed(1)}% of one year&apos;s cost of inaction
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Final CTA ── */}
        <motion.div {...fadeUp(0.42)} className="mb-14">
          <div
            className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #0f172a 100%)",
            }}
          >
            <div
              className="absolute inset-0 opacity-[0.03] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle, white 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
              aria-hidden="true"
            />
            <div className="relative">
              <Badge className="mb-5 bg-blue-500/15 text-[#3b82f6] border border-blue-500/20 inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                Your blueprint is already built
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Don&apos;t let it sit unlocked.
              </h2>
              <p className="text-slate-400 max-w-md mx-auto mb-8 text-sm">
                {businessName
                  ? `${possessive(businessName)} blueprint has ${phaseCount || 3} phases, ${totalToolsCount} tools, and ${playbookCount} playbooks ready to go — it takes 60 seconds to unlock.`
                  : `Your blueprint has ${phaseCount || 3} phases, ${totalToolsCount} tools, and ${playbookCount} playbooks ready to go — it takes 60 seconds to unlock.`}
              </p>
              {/* Final price */}
              <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
                <span className="text-2xl font-bold text-[#3b82f6]">
                  {formatCurrency(displayPrice)}
                </span>
                <span className="text-xs text-blue-400 font-semibold">
                  Launch pricing — one-time, no subscription
                </span>
                {y1Net > 0 && (
                  <span className="text-xs text-slate-400 bg-white/5 px-2 py-1 rounded-full border border-white/10">
                    {(() => {
                      const roi = fullReport?.roiProjections || {};
                      const laborSavings  = roi.estimated_monthly_savings        || 0;
                      const revenueUplift = roi.estimated_monthly_revenue_uplift || 0;
                      const annual        = (laborSavings + revenueUplift) * 12;
                      const toolCost      = roi.y1_investment_estimate           || 0;
                      const reportPrice   = displayPrice                         || 197;

                      if (annual > 0 && toolCost > 0) {
                        const totalInvestment = reportPrice + toolCost;
                        const netMultiple = Math.round(annual / totalInvestment);
                        if (netMultiple <= 50) {
                          return (
                            <span>
                              {netMultiple}x projected return
                              <span className="text-[10px] font-normal ml-1 opacity-60">
                                ({formatCurrency(annual)}/yr ÷ {formatCurrency(totalInvestment)} total investment)
                              </span>
                            </span>
                          );
                        }
                      }

                      // Fallback: payback period instead of inflated multiplier
                      const annualFallback = annual > 0 ? annual : y1Net * 12;
                      const paybackDays = annualFallback > 0
                        ? Math.round(reportPrice / (annualFallback / 365))
                        : null;
                      return paybackDays ? (
                        <span>
                          {paybackDays}-day payback
                          <span className="text-[10px] font-normal ml-1 opacity-60">
                            on {formatCurrency(displayPrice)} report cost
                          </span>
                        </span>
                      ) : (
                        <span>High projected ROI</span>
                      );
                    })()}
                  </span>
                )}
              </div>

              <Button
                onClick={goToPayment}
                className="bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 text-xl font-bold px-12 py-6 shadow-2xl hover:shadow-blue-500/40 transition-all rounded-full mb-5"
              >
                Get My Blueprint &mdash; Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              {/* Visual guarantee badge */}
              <div className="inline-flex items-center gap-3 bg-white/5 border border-blue-500/20 rounded-2xl px-6 py-3">
                <Shield className="w-6 h-6 text-[#3b82f6] flex-shrink-0" />
                <div className="text-left">
                  <div className="text-xs font-bold text-white">60-Day Implementation Guarantee</div>
                  <div className="text-xs text-slate-400">Follow Phase 1. If you don&apos;t see value, we&apos;ll troubleshoot or refund you in full.</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

      </main>

      {/* Desktop Floating CTA */}
      <div className="hidden md:block fixed bottom-8 right-8 z-50">
        <div className="text-right mb-1.5">
          <p className="text-xs text-slate-400">60-Day Implementation Guarantee</p>
        </div>
        <Button
          onClick={goToPayment}
          className="bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 font-bold px-8 py-5 shadow-2xl rounded-full text-base transition-all hover:shadow-blue-500/40"
        >
          {businessName
            ? `Unlock ${possessive(businessName)} Blueprint`
            : "Unlock My Blueprint"}{" "}
          &mdash; {formatCurrency(displayPrice)}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>

      <details className="max-w-2xl mx-auto mt-8 mb-4 px-4">
        <summary className="text-xs text-slate-600 hover:text-slate-400 cursor-pointer transition-colors list-none flex items-center gap-1.5">
          <ChevronRight className="w-3 h-3" />
          How we calculate these estimates
        </summary>
        <div className="mt-3 text-[11px] text-slate-600 leading-relaxed space-y-2 bg-slate-950/40 rounded-xl p-4 border border-white/5">
          <p>
            <strong className="text-slate-500">Labor savings</strong> are
            estimated by identifying tasks in your workflow that have
            documented AI automation rates, multiplying by hours reported,
            and applying the Bureau of Labor Statistics wage benchmark for
            your industry ({fullReport?.roiProjections?.labor_benchmark_hourly
              ? `$${fullReport.roiProjections.labor_benchmark_hourly}/hr`
              : "your industry rate"}).
          </p>
          <p>
            <strong className="text-slate-500">Revenue uplift</strong> is
            estimated using the conservative lower bound of observed AI
            adoption lift rates across businesses in your industry and
            revenue band from our benchmark dataset. We use the 25th
            percentile — not the average — to avoid overstating results.
          </p>
          <p>
            <strong className="text-slate-500">These are estimates,
            not guarantees.</strong> They are meant to illustrate
            the order of magnitude of opportunity, not to predict
            exact outcomes. Actual results depend on implementation
            quality, staff adoption, and market conditions.
          </p>
        </div>
      </details>

      <Footer />

      {/* R47 (free pivot): email-unlock modal. Single email field +
          invisible honeypot. Submits to /api/audit/report/{id}/unlock
          which has server-side disposable-email blocklist + per-IP
          + per-email rate limits + min-time gate. Used to be a
          redirect to /payment/{id} with $149 Stripe checkout. */}
      {showUnlockModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowUnlockModal(false); }}
        >
          <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl p-6 md:p-7">
            <div className="flex items-start justify-between gap-4 mb-1">
              <h3 className="text-xl md:text-2xl font-bold text-white">
                Unlock your free blueprint
              </h3>
              <button
                onClick={() => setShowUnlockModal(false)}
                className="text-slate-400 hover:text-white text-2xl leading-none -mt-1"
                aria-label="Close"
                type="button"
              >&times;</button>
            </div>
            <p className="text-sm text-slate-400 mb-5">
              Enter your email and we'll generate your full personalized
              AI implementation blueprint. No payment, no credit card &mdash;
              if it helps you, leave a tip after.
            </p>
            <form onSubmit={handleUnlockSubmit} className="space-y-3">
              <input
                type="email"
                required
                autoFocus
                value={unlockEmail}
                onChange={(e) => setUnlockEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                className="w-full bg-slate-950 border border-white/15 text-white placeholder-slate-500 rounded-xl px-4 py-3 text-base focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                autoComplete="email"
                inputMode="email"
              />
              {/* Honeypot — invisible to humans, irresistible to bots.
                  Any non-empty value here triggers server-side
                  silent-reject. aria-hidden + tabIndex=-1 keep
                  assistive tech and keyboard users away from it. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={unlockHoneypot}
                onChange={(e) => setUnlockHoneypot(e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                aria-hidden="true"
              />
              <button
                type="submit"
                disabled={unlockSubmitting}
                className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 text-white font-bold py-3.5 rounded-xl text-base shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {unlockSubmitting ? "Unlocking…" : "Get my free blueprint →"}
              </button>
              <p className="text-[11px] text-slate-500 text-center leading-relaxed pt-1">
                We&apos;ll email you the link so you can return anytime. No spam,
                no credit card. Free forever &mdash; tips appreciated, not required.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPreviewPage;
